/* biome-ignore-all lint/suspicious/noExplicitAny: log preview data */
import type React from "react"
import { useState, useEffect, useMemo } from "react"
import {
  Modal,
  Table,
  Tag,
  Typography,
  Button,
  Spin,
  Alert,
  Input,
  Radio,
  Space,
  Tooltip,
  Divider,
  Image,
  Empty,
  message,
} from "antd"
import {
  EyeOutlined,
  DownloadOutlined,
  CopyOutlined,
  ReloadOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ExportOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileZipOutlined,
  PictureOutlined,
  LeftOutlined,
  RightOutlined,
  MessageOutlined,
  HistoryOutlined,
} from "@ant-design/icons"
import type { ColumnsType } from "antd/es/table"
import { UserCellLink } from "@/components/UserCellLink"
import { SOURCE_TYPE_MAP, STATUS_MAP, type AddVocabularyLogItem } from "./AddVocabularyTable"
import dayjs from "dayjs"
import "@/styles/table.full.css"

const { Text } = Typography

export const getDownloadUrl = (path: string) => {
  if (!path) return ""
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path.replace(/^http:\/\//i, "https://")
  }
  return `https://cdn.jupi.vn/${path.replace(/^\/+/, "")}`
}

export interface PreviewItem {
  line_no?: number
  raw_text?: string
  text?: string
  status?: string
  meaning?: string | null
  part_of_speech?: string | null
  example?: string | null
  example_meaning?: string | null
  synonyms?: string | string[] | any[] | null
  synonym?: string | string[] | any[] | null
  antonyms?: string | string[] | any[] | null
  antonym?: string | string[] | any[] | null
  error_message?: string | null
}

export interface PreviewOutputData {
  total_items?: number
  items?: PreviewItem[]
}

export interface AddVocabularyDetailModalProps {
  open: boolean
  record: AddVocabularyLogItem | null
  items?: AddVocabularyLogItem[]
  currentIndex?: number
  onNavigate?: (index: number) => void
  onClose: () => void
}

const renderWordList = (data: any, tagColor: string) => {
  if (!data) return null
  let list: string[] = []
  if (Array.isArray(data)) {
    list = data
      .map((item) => (typeof item === "string" ? item : item?.word || item?.text || JSON.stringify(item)))
      .filter(Boolean)
  } else if (typeof data === "string" && data.trim()) {
    try {
      const parsed = JSON.parse(data)
      if (Array.isArray(parsed)) {
        list = parsed
          .map((item) => (typeof item === "string" ? item : item?.word || item?.text || JSON.stringify(item)))
          .filter(Boolean)
      } else {
        list = data
          .split(/[,;\n]+/)
          .map((s) => s.trim())
          .filter(Boolean)
      }
    } catch {
      list = data
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean)
    }
  }
  if (list.length === 0) return null
  return list.map((w, i) => (
    <Tag key={i} color={tagColor} className="m-0 px-1.5 py-0 text-[11px] leading-snug">
      {w}
    </Tag>
  ))
}

export const PREVIEW_STATUS_CONFIG: Record<
  string,
  {
    label: string
    color: string
    icon: React.ReactNode
    description: string
  }
> = {
  valid_new: {
    label: "Hợp lệ",
    color: "success",
    icon: <CheckCircleOutlined />,
    description: "Hợp lệ (Sẵn sàng lưu): Tìm thấy nghĩa trong từ điển, sẵn sàng để lưu vào bộ.",
  },
  not_found: {
    label: "Không tìm thấy",
    color: "error",
    icon: <CloseCircleOutlined />,
    description: "Không tìm thấy trong từ điển (gõ sai chính tả, từ lóng, file lỗi format...).",
  },
  already_in_target_set: {
    label: "Đã có trong bộ",
    color: "blue",
    icon: <InfoCircleOutlined />,
    description: "Đã có trong bộ: Từ này đã tồn tại trong bộ từ của người dùng.",
  },
  duplicate_in_input: {
    label: "Trùng lặp",
    color: "warning",
    icon: <WarningOutlined />,
    description: "Trùng lặp: Bị lặp từ ngay trong danh sách/file vừa nhập.",
  },
}

export const normalizePreviewStatus = (status?: string): string => {
  const s = String(status || "")
    .trim()
    .toLowerCase()
  if (s === "valid_new" || s === "ready" || s === "valid" || s === "success") return "valid_new"
  if (s === "not_found" || s === "missing") return "not_found"
  if (s === "already_in_target_set" || s === "exists" || s === "already_exists") return "already_in_target_set"
  if (s === "duplicate_in_input" || s === "duplicate") return "duplicate_in_input"
  return s || "other"
}

export interface VocabularyLogPayloadParsed {
  userPrompt: string | null
  editPrompts: string[]
}

export const parseVocabularyLogPayload = (payload: any): VocabularyLogPayloadParsed => {
  if (!payload) {
    return { userPrompt: null, editPrompts: [] }
  }

  let obj: any = payload
  if (typeof payload === "string") {
    try {
      obj = JSON.parse(payload)
    } catch {
      return { userPrompt: null, editPrompts: [] }
    }
  }

  if (!obj || typeof obj !== "object") {
    return { userPrompt: null, editPrompts: [] }
  }

  const userPrompt =
    typeof obj.user_prompt === "string" && obj.user_prompt.trim()
      ? obj.user_prompt.trim()
      : null

  let editPrompts: string[] = []
  if (Array.isArray(obj.edit_prompts)) {
    editPrompts = obj.edit_prompts
      .map((item: any) => (typeof item === "string" ? item.trim() : String(item || "").trim()))
      .filter((s: string) => Boolean(s))
  }

  return { userPrompt, editPrompts }
}

export const getUserPrompt = (payload: any): string | null => {
  return parseVocabularyLogPayload(payload).userPrompt
}

export const AddVocabularyDetailModal: React.FC<AddVocabularyDetailModalProps> = ({
  open,
  record,
  items = [],
  currentIndex = 0,
  onNavigate,
  onClose,
}) => {
  const totalRecords = items.length
  const hasPrev = typeof currentIndex === "number" && currentIndex > 0
  const hasNext = typeof currentIndex === "number" && currentIndex < totalRecords - 1

  const handlePrev = () => {
    if (hasPrev && onNavigate) {
      onNavigate(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(currentIndex + 1)
    }
  }

  // Keyboard shortcut: ArrowLeft / ArrowRight to navigate logs
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      const tagName = target?.tagName?.toUpperCase()
      if (tagName === "INPUT" || tagName === "TEXTAREA" || target?.isContentEditable) {
        return
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault()
        handlePrev()
      } else if (e.key === "ArrowRight") {
        e.preventDefault()
        handleNext()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, hasPrev, hasNext, currentIndex, onNavigate])

  // Input states
  const [inputLoading, setInputLoading] = useState(false)
  const [textContent, setTextContent] = useState<string | null>(null)
  const [inputError, setInputError] = useState<string | null>(null)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)

  // Output states
  const [outputLoading, setOutputLoading] = useState(false)
  const [outputData, setOutputData] = useState<PreviewOutputData | null>(null)
  const [outputError, setOutputError] = useState<string | null>(null)
  const [searchText, setSearchText] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const rawInputPath = record?.raw_input_path
  const rawOutputPath = record?.output_path

  const inputUrl = useMemo(() => (rawInputPath ? getDownloadUrl(rawInputPath) : ""), [rawInputPath])
  const inputFileName = useMemo(
    () => (rawInputPath ? rawInputPath.split("/").pop() || "raw_input" : ""),
    [rawInputPath]
  )

  const outputUrl = useMemo(() => (rawOutputPath ? getDownloadUrl(rawOutputPath) : ""), [rawOutputPath])
  const outputFileName = useMemo(
    () => (rawOutputPath ? rawOutputPath.split("/").pop() || "output.json" : ""),
    [rawOutputPath]
  )

  // File type detection
  const inputType = useMemo(() => {
    if (!rawInputPath) return "empty"
    const lower = rawInputPath.toLowerCase()
    const srcType = record?.source_type || ""

    if (lower.endsWith(".pdf") || srcType === "ai_pdf") return "pdf"
    if (lower.match(/\.(png|jpe?g|webp|gif|bmp|svg)$/i) || srcType === "ai_image") return "image"
    if (lower.match(/\.(xlsx|xls|csv)$/i) || srcType === "import_excel") return lower.endsWith(".csv") ? "text" : "excel"
    if (lower.match(/\.(apkg|zip|rar|7z)$/i) || srcType === "import_anki") return "archive"
    if (
      lower.match(/\.(txt|tsv|json)$/i) ||
      srcType === "input_text" ||
      srcType === "manual" ||
      srcType === "import_anki_text" ||
      srcType === "import_quizlet" ||
      srcType === "ai_extract_text" ||
      srcType === "ai_chat" ||
      !lower.includes(".")
    ) {
      return "text"
    }
    return "other"
  }, [rawInputPath, record?.source_type])

  // Fetch Text Input Content
  const fetchInputText = async () => {
    if (!inputUrl) return
    setInputLoading(true)
    setInputError(null)
    setTextContent(null)

    try {
      const response = await fetch(inputUrl)
      if (!response.ok) {
        throw new Error(`Không thể tải file input: HTTP ${response.status}`)
      }
      const text = await response.text()
      setTextContent(text)
    } catch (err: any) {
      setInputError(err?.message || "Lỗi khi đọc nội dung input từ CDN")
    } finally {
      setInputLoading(false)
    }
  }

  // Fetch PDF Blob to ensure inline viewing without server download-forcing
  const fetchPdfBlob = async () => {
    if (!inputUrl) return
    setPdfLoading(true)
    setPdfError(null)

    try {
      const response = await fetch(inputUrl)
      if (!response.ok) {
        throw new Error(`Không thể tải file PDF: HTTP ${response.status}`)
      }
      const blob = await response.blob()
      const pdfBlob = new Blob([blob], { type: "application/pdf" })
      const objectUrl = URL.createObjectURL(pdfBlob)
      setPdfBlobUrl(objectUrl)
    } catch (err: any) {
      setPdfError(err?.message || "Lỗi khi tải file PDF từ CDN")
    } finally {
      setPdfLoading(false)
    }
  }

  // Fetch Output JSON
  const fetchOutputData = async () => {
    if (!outputUrl) return
    setOutputLoading(true)
    setOutputError(null)
    setOutputData(null)

    try {
      const response = await fetch(outputUrl)
      if (!response.ok) {
        throw new Error(`Không thể tải file output: HTTP ${response.status}`)
      }
      const json: PreviewOutputData = await response.json()
      setOutputData(json)
    } catch (err: any) {
      setOutputError(err?.message || "Lỗi khi đọc kết quả output từ CDN")
    } finally {
      setOutputLoading(false)
    }
  }

  useEffect(() => {
    if (open && record) {
      setSearchText("")
      setStatusFilter("all")

      if (rawInputPath && inputType === "text") {
        fetchInputText()
      } else {
        setTextContent(null)
        setInputError(null)
      }

      if (rawInputPath && inputType === "pdf") {
        fetchPdfBlob()
      } else {
        if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl)
        setPdfBlobUrl(null)
        setPdfError(null)
      }

      if (rawOutputPath) {
        fetchOutputData()
      } else {
        setOutputData(null)
        setOutputError(null)
      }
    } else {
      setTextContent(null)
      setOutputData(null)
      setInputError(null)
      setOutputError(null)
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl)
      setPdfBlobUrl(null)
      setPdfError(null)
    }
  }, [open, record, inputType])

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [pdfBlobUrl])

  // Copy text content
  const handleCopyInput = async () => {
    if (!textContent) return
    try {
      await navigator.clipboard.writeText(textContent)
      message.success("Đã sao chép toàn bộ nội dung input vào clipboard!")
    } catch {
      message.error("Không thể sao chép văn bản")
    }
  }

  // Extract user_prompt & edit_prompts from record?.payload
  const { userPrompt, editPrompts } = useMemo(
    () => parseVocabularyLogPayload(record?.payload),
    [record?.payload]
  )

  const isAiSource =
    record?.source_type === "ai_image" || Boolean(record?.source_type?.startsWith("ai_"))
  const hasPromptData = Boolean(userPrompt || editPrompts.length > 0)
  const shouldShowPromptSection = isAiSource || hasPromptData

  const handleCopyText = async (text: string, successMsg = "Đã sao chép vào clipboard!") => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      message.success(successMsg)
    } catch {
      message.error("Không thể sao chép văn bản")
    }
  }

  // Output stats
  const stats = useMemo(() => {
    const items = outputData?.items || []
    let validNewCount = 0
    let notFoundCount = 0
    let alreadyInSetCount = 0
    let duplicateCount = 0
    let otherCount = 0

    items.forEach((item) => {
      const s = normalizePreviewStatus(item.status)
      if (s === "valid_new") validNewCount++
      else if (s === "not_found") notFoundCount++
      else if (s === "already_in_target_set") alreadyInSetCount++
      else if (s === "duplicate_in_input") duplicateCount++
      else otherCount++
    })

    return {
      total: outputData?.total_items ?? items.length,
      validNewCount,
      notFoundCount,
      alreadyInSetCount,
      duplicateCount,
      otherCount,
    }
  }, [outputData])

  // Filtered Output items
  const filteredItems = useMemo(() => {
    let items = outputData?.items || []

    if (statusFilter !== "all") {
      items = items.filter((item) => normalizePreviewStatus(item.status) === statusFilter)
    }

    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase()
      items = items.filter(
        (item) =>
          String(item.raw_text || item.text || "").toLowerCase().includes(query) ||
          String(item.meaning || "").toLowerCase().includes(query) ||
          String(item.example || "").toLowerCase().includes(query) ||
          String(item.example_meaning || "").toLowerCase().includes(query) ||
          String(JSON.stringify(item.synonyms || item.synonym || "")).toLowerCase().includes(query) ||
          String(JSON.stringify(item.antonyms || item.antonym || "")).toLowerCase().includes(query) ||
          String(item.error_message || "").toLowerCase().includes(query) ||
          String(item.part_of_speech || "").toLowerCase().includes(query)
      )
    }

    return items
  }, [outputData, statusFilter, searchText])

  // Text Lines
  const textLines = useMemo(() => {
    if (!textContent) return []
    return textContent.split(/\r?\n/)
  }, [textContent])

  const outputColumns: ColumnsType<PreviewItem> = [
    {
      title: "#",
      dataIndex: "line_no",
      width: 45,
      align: "center",
      render: (val, _, idx) => (
        <span className="font-mono text-gray-500 text-xs">{val !== undefined && val !== null ? val : idx + 1}</span>
      ),
    },
    {
      title: "Từ vựng (Gốc)",
      dataIndex: "raw_text",
      width: 130,
      render: (val, row) => (
        <span className="font-mono font-semibold text-gray-900 text-sm">{val || row.text || "—"}</span>
      ),
    },
    {
      title: "Loại",
      dataIndex: "part_of_speech",
      width: 75,
      align: "center",
      render: (val) => (val ? <Tag color="blue" className="m-0 text-[11px]">{val}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: "Nghĩa tiếng Việt",
      dataIndex: "meaning",
      width: 170,
      render: (val) =>
        val ? (
          <span className="text-gray-800 text-xs leading-snug">{val}</span>
        ) : (
          <Text type="secondary" italic className="text-xs">
            Chưa có
          </Text>
        ),
    },
    {
      title: "Ví dụ & Dịch",
      key: "example",
      width: 220,
      render: (_, row) => {
        const example = row.example
        const exampleMeaning = row.example_meaning
        if (!example && !exampleMeaning) return <Text type="secondary">—</Text>

        return (
          <div className="flex flex-col gap-0.5 text-xs leading-relaxed">
            {example && <div className="font-serif italic text-gray-800">“{example}”</div>}
            {exampleMeaning && <div className="text-[11px] text-gray-500">↳ {exampleMeaning}</div>}
          </div>
        )
      },
    },
    {
      title: "Đồng / Trái nghĩa",
      key: "thesaurus",
      width: 170,
      render: (_, row) => {
        const syn = row.synonyms || row.synonym
        const ant = row.antonyms || row.antonym
        const renderedSyn = renderWordList(syn, "green")
        const renderedAnt = renderWordList(ant, "volcano")

        if (!renderedSyn && !renderedAnt) return <Text type="secondary">—</Text>

        return (
          <div className="flex flex-wrap gap-1 items-center">
            {renderedSyn}
            {renderedAnt}
          </div>
        )
      },
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: 120,
      align: "center",
      render: (val) => {
        const key = normalizePreviewStatus(val)
        const config = PREVIEW_STATUS_CONFIG[key]
        if (config) {
          return (
            <Tooltip title={config.description} placement="top">
              <Tag color={config.color} icon={config.icon} className="cursor-help font-medium m-0 text-xs">
                {config.label}
              </Tag>
            </Tooltip>
          )
        }
        return <Tag className="m-0 text-xs">{val || "—"}</Tag>
      },
    },
    {
      title: "Lỗi / Ghi chú",
      dataIndex: "error_message",
      width: 120,
      render: (val) => (val ? <Text type="danger" className="text-xs">{val}</Text> : <Text type="secondary">—</Text>),
    },
  ]

  const sourceInfo = record?.source_type ? SOURCE_TYPE_MAP[record.source_type] : null
  const statusInfo = record?.status ? STATUS_MAP[record.status] : null

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="100vw"
      style={{
        top: 0,
        left: 0,
        margin: 0,
        padding: 0,
        maxWidth: "100vw",
        height: "100vh",
      }}
      styles={{
        wrapper: {
          overflow: "hidden",
        },
        container: {
          height: "100vh",
          maxHeight: "100vh",
          width: "100vw",
          maxWidth: "100vw",
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          padding: "14px 18px 10px 18px",
          overflow: "hidden",
        },
        header: {
          margin: 0,
          padding: 0,
          flexShrink: 0,
        },
        body: {
          flex: 1,
          minHeight: 0,
          height: "100%",
          overflow: "hidden",
          padding: "10px 0",
          display: "flex",
          flexDirection: "column",
        },
        footer: {
          margin: 0,
          padding: 0,
          flexShrink: 0,
        },
      }}
      title={
        <div className="flex flex-wrap items-center justify-between gap-3 border-gray-200 border-b pb-2 pr-6">
          <div className="flex items-center gap-2">
            <span className="font-bold text-gray-800 text-lg">📄 Chi tiết Log nhập từ vựng #{record?.id}</span>
            {record?.status && (
              <Tag color={statusInfo?.color || "default"} className="font-medium text-xs">
                {statusInfo?.label || record.status}
              </Tag>
            )}
            {sourceInfo && (
              <Tag color={sourceInfo.color} className="font-medium text-xs">
                Nguồn: {sourceInfo.label}
              </Tag>
            )}

            {totalRecords > 1 && typeof currentIndex === "number" && (
              <Space size={4} className="ml-2">
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  onClick={handlePrev}
                  disabled={!hasPrev}
                  title="Bản ghi trước (Phím ←)"
                >
                  Trước
                </Button>
                <span className="font-mono text-xs text-gray-500 font-medium px-1">
                  {currentIndex + 1} / {totalRecords}
                </span>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  onClick={handleNext}
                  disabled={!hasNext}
                  title="Bản ghi tiếp theo (Phím →)"
                >
                  Tiếp
                </Button>
              </Space>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="text-gray-500">Người dùng:</span>
              <UserCellLink user={record?.user} userId={record?.user_id} fallback={<Tag color="default">Khách</Tag>} />
            </div>

            <Divider orientation="vertical" className="m-0" />

            <div className="flex items-center gap-1">
              <span className="text-gray-500">Bộ từ:</span>
              <strong className="text-gray-800">{record?.set?.title || record?.set?.name || "Chưa gán"}</strong>
            </div>

            <Divider orientation="vertical" className="m-0" />

            <div className="flex items-center gap-1">
              <span className="text-gray-500">Thời gian:</span>
              <span className="font-mono text-gray-700">
                {record?.created_at ? dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss") : "—"}
              </span>
            </div>

            {record?.duration_ms && (
              <>
                <Divider orientation="vertical" className="m-0" />
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">Xử lý:</span>
                  <span className="font-mono text-emerald-600 font-medium">{record.duration_ms}ms</span>
                </div>
              </>
            )}
          </div>
        </div>
      }
      footer={
        <div className="flex items-center justify-between border-gray-200 border-t pt-2">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {rawInputPath && (
              <span className="truncate max-w-sm" title={rawInputPath}>
                Input: <code className="text-gray-700">{inputFileName}</code>
              </span>
            )}
            {rawOutputPath && (
              <span className="truncate max-w-sm" title={rawOutputPath}>
                Output: <code className="text-gray-700">{outputFileName}</code>
              </span>
            )}
          </div>
          <Button type="primary" onClick={onClose}>
            Đóng
          </Button>
        </div>
      }
    >
      {/* Thông tin Prompt & Lịch sử sửa bằng AI */}
      {shouldShowPromptSection && (
        <div className="mb-2.5 flex flex-col gap-2 rounded-lg border border-purple-200 bg-purple-50/60 p-2.5 text-xs shadow-xs shrink-0">
          {/* Prompt ban đầu */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-1 items-start gap-2 min-w-0">
              <span className="font-semibold text-purple-900 shrink-0 flex items-center gap-1.5 pt-0.5">
                <MessageOutlined className="text-purple-600" />
                Prompt ban đầu:
              </span>
              {userPrompt ? (
                <div className="font-medium text-gray-800 break-words whitespace-pre-wrap leading-relaxed select-text flex-1">
                  {userPrompt}
                </div>
              ) : (
                <span className="text-gray-400 font-medium">—</span>
              )}
            </div>
            {userPrompt && (
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => handleCopyText(userPrompt, "Đã sao chép prompt ban đầu!")}
                className="text-purple-700 hover:text-purple-900 shrink-0 h-6 px-1.5 text-xs"
                title="Sao chép prompt ban đầu"
              >
                Sao chép
              </Button>
            )}
          </div>

          <Divider className="my-0.5 border-purple-100" />

          {/* Lịch sử sửa bằng AI */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-1 items-start gap-2 min-w-0">
              <span className="font-semibold text-purple-900 shrink-0 flex items-center gap-1.5 pt-0.5">
                <HistoryOutlined className="text-purple-600" />
                Lịch sử sửa bằng AI:
              </span>
              {editPrompts.length > 0 ? (
                <div className="flex flex-1 flex-col gap-1.5 min-w-0 max-h-36 overflow-y-auto pr-1">
                  {editPrompts.map((prompt, idx) => (
                    <div
                      key={idx}
                      className="group flex items-start justify-between gap-2 rounded bg-white/90 border border-purple-100 px-2 py-1 select-text"
                    >
                      <div className="flex-1 min-w-0 break-words leading-relaxed text-gray-800">
                        <Tag color="purple" className="mr-1.5 text-[11px] font-semibold py-0 leading-normal">
                          Lần {idx + 1}
                        </Tag>
                        <span className="font-medium">{prompt}</span>
                      </div>
                      <Button
                        size="small"
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyText(prompt, `Đã sao chép prompt lần ${idx + 1}!`)}
                        className="text-purple-600 hover:text-purple-800 opacity-60 group-hover:opacity-100 shrink-0 h-5 px-1 text-[11px]"
                        title={`Sao chép prompt lần ${idx + 1}`}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400 font-medium">— (chưa từng chat sửa)</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2 COLUMNS SPLIT VIEW */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1 min-h-0">
        {/* LEFT COLUMN: INPUT */}
        <div className="flex flex-col h-full min-h-0 rounded-lg border border-gray-200 bg-gray-50/50 p-2.5 shadow-xs">
          {/* Input Header */}
          <div className="flex items-center justify-between border-gray-200 border-b pb-2 mb-2">
            <div className="flex items-center gap-1.5">
              {inputType === "pdf" && <FilePdfOutlined className="text-rose-500 text-base" />}
              {inputType === "text" && <FileTextOutlined className="text-blue-500 text-base" />}
              {inputType === "excel" && <FileExcelOutlined className="text-emerald-500 text-base" />}
              {inputType === "archive" && <FileZipOutlined className="text-amber-500 text-base" />}
              {inputType === "image" && <PictureOutlined className="text-purple-500 text-base" />}
              <span className="font-bold text-gray-800 text-sm">📥 DỮ LIỆU ĐẦU VÀO (INPUT)</span>
              {inputFileName && (
                <span className="font-mono text-[11px] text-gray-500 truncate max-w-[150px]" title={inputFileName}>
                  ({inputFileName})
                </span>
              )}
            </div>

            <Space size={4}>
              {inputType === "text" && textContent && (
                <Button size="small" icon={<CopyOutlined />} onClick={handleCopyInput}>
                  Sao chép
                </Button>
              )}
              {inputUrl && (
                <>
                  <Button
                    size="small"
                    icon={<ExportOutlined />}
                    href={inputType === "pdf" && pdfBlobUrl ? pdfBlobUrl : inputUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Mở tài liệu sang tab mới"
                  >
                    Mở tab mới
                  </Button>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    href={inputUrl}
                    download={inputFileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Tải file gốc về máy"
                  >
                    Tải về
                  </Button>
                </>
              )}
            </Space>
          </div>

          {/* Input Body Content */}
          <div className="flex-1 min-h-0 relative flex flex-col">
            {inputLoading && (
              <div className="flex flex-1 flex-col items-center justify-center p-8">
                <Spin />
                <span className="mt-3 text-gray-500 text-xs">Đang tải dữ liệu input...</span>
              </div>
            )}

            {!inputLoading && inputError && (
              <Alert
                type="error"
                showIcon
                message="Không thể đọc nội dung file input"
                description={
                  <div className="flex flex-col gap-1.5 text-xs">
                    <span>{inputError}</span>
                    <Button size="small" icon={<ReloadOutlined />} onClick={fetchInputText} className="w-fit mt-1">
                      Thử lại
                    </Button>
                  </div>
                }
              />
            )}

            {!inputLoading && !inputError && (
              <>
                {/* PDF Viewer via Blob Object URL */}
                {inputType === "pdf" && (
                  <div className="flex-1 min-h-0 bg-gray-100 rounded overflow-hidden border border-gray-200 relative flex flex-col">
                    {pdfLoading && (
                      <div className="flex flex-1 flex-col items-center justify-center p-8 bg-white">
                        <Spin />
                        <span className="mt-3 text-gray-500 text-xs">Đang tải và chuẩn bị tài liệu PDF...</span>
                      </div>
                    )}

                    {!pdfLoading && pdfError && (
                      <div className="p-4 bg-white flex-1">
                        <Alert
                          type="error"
                          showIcon
                          message="Không thể hiển thị tài liệu PDF"
                          description={
                            <div className="flex flex-col gap-2 text-xs mt-1">
                              <span>{pdfError}</span>
                              <Button size="small" icon={<ReloadOutlined />} onClick={fetchPdfBlob} className="w-fit">
                                Thử lại
                              </Button>
                            </div>
                          }
                        />
                      </div>
                    )}

                    {!pdfLoading && !pdfError && (
                      <iframe
                        src={pdfBlobUrl || inputUrl}
                        className="w-full h-full border-0"
                        title="PDF Document Viewer"
                      />
                    )}
                  </div>
                )}

                {/* Text Reader */}
                {inputType === "text" && (
                  <div className="flex-1 min-h-0 flex flex-col rounded border border-gray-200 bg-white">
                    <div className="border-gray-200 border-b bg-gray-50 px-3 py-1 text-[11px] text-gray-500 flex justify-between items-center">
                      <span>Văn bản đầu vào: <strong>{textLines.length}</strong> dòng</span>
                      <span>{textContent?.length || 0} ký tự</span>
                    </div>
                    <div className="flex-1 min-h-0 overflow-auto font-mono text-xs p-2 leading-relaxed select-text bg-[#fafafa]">
                      {textLines.length > 0 ? (
                        textLines.map((line, idx) => (
                          <div key={idx} className="flex hover:bg-blue-50/60 rounded px-1">
                            <span className="w-10 select-none text-right pr-3 text-gray-400 font-mono text-[11px]">
                              {idx + 1}
                            </span>
                            <span className="flex-1 whitespace-pre-wrap break-all text-gray-800">{line}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-400 text-center py-8">Tập tin trống không có nội dung</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Image Viewer */}
                {inputType === "image" && (
                  <div className="flex-1 min-h-0 flex items-center justify-center bg-gray-900/5 rounded border border-gray-200 p-2 overflow-auto">
                    <Image
                      src={inputUrl}
                      alt="Input Image"
                      className="max-h-[calc(100vh-220px)] object-contain rounded"
                    />
                  </div>
                )}

                {/* Excel / Anki / Binary File Card */}
                {(inputType === "excel" || inputType === "archive" || inputType === "other") && (
                  <div className="flex-1 min-h-0 flex flex-col items-center justify-center rounded border border-dashed border-gray-300 bg-white p-6 text-center">
                    {inputType === "excel" && <FileExcelOutlined className="text-emerald-500 text-5xl mb-3" />}
                    {inputType === "archive" && <FileZipOutlined className="text-amber-500 text-5xl mb-3" />}
                    {inputType === "other" && <FileTextOutlined className="text-blue-500 text-5xl mb-3" />}

                    <span className="font-semibold text-gray-800 text-base mb-1">{inputFileName}</span>
                    <span className="text-gray-500 text-xs mb-4 max-w-xs">
                      {inputType === "excel"
                        ? "Tệp bảng tính Excel chứa danh sách từ vựng gốc."
                        : inputType === "archive"
                          ? "Gói dữ liệu thẻ flashcard Anki (.apkg)."
                          : "Tệp dữ liệu đầu vào định dạng nhị phân."}
                    </span>

                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      href={inputUrl}
                      download={inputFileName}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="large"
                    >
                      Tải tệp gốc xuống máy
                    </Button>
                  </div>
                )}

                {/* Empty State */}
                {inputType === "empty" && (
                  <div className="flex-1 min-h-0 flex flex-col items-center justify-center">
                    <Empty description="Không có tệp dữ liệu đầu vào cho lượt log này" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN: OUTPUT */}
        <div className="flex flex-col h-full min-h-0 rounded-lg border border-gray-200 bg-gray-50/50 p-2.5 shadow-xs">
          {/* Output Header */}
          <div className="flex items-center justify-between border-gray-200 border-b pb-2 mb-2">
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-gray-800 text-sm">📤 KẾT QUẢ TỪ VỰNG (OUTPUT)</span>
              {stats.total > 0 && (
                <Tag color="blue" className="font-mono font-semibold m-0 text-xs">
                  {stats.total} từ
                </Tag>
              )}
            </div>

            <Space size={4}>
              {outputUrl && (
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  href={outputUrl}
                  download={outputFileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Tải toàn bộ file JSON Output"
                >
                  Tải JSON Output
                </Button>
              )}
            </Space>
          </div>

          {/* Output Body Content */}
          <div className="flex-1 min-h-0 flex flex-col gap-2">
            {outputLoading && (
              <div className="flex flex-1 flex-col items-center justify-center p-8">
                <Spin />
                <span className="mt-3 text-gray-500 text-xs">Đang tải danh sách kết quả Preview...</span>
              </div>
            )}

            {!outputLoading && outputError && (
              <Alert
                type="error"
                showIcon
                message="Không thể đọc kết quả Output"
                description={
                  <div className="flex flex-col gap-1.5 text-xs">
                    <span>{outputError}</span>
                    <Button size="small" icon={<ReloadOutlined />} onClick={fetchOutputData} className="w-fit mt-1">
                      Thử lại
                    </Button>
                  </div>
                }
              />
            )}

            {!outputLoading && !outputError && outputData && (
              <>
                {/* Stats Summary Bar */}
                <div className="grid grid-cols-2 gap-1 rounded border border-gray-200 bg-white p-1 text-center sm:grid-cols-5 text-xs">
                  <div className="flex flex-col items-center justify-center p-0.5">
                    <span className="text-gray-500 text-[10px]">Tổng</span>
                    <span className="font-bold font-mono text-gray-800 text-sm">{stats.total}</span>
                  </div>
                  <Tooltip title="Hợp lệ trong từ điển">
                    <div className="flex cursor-help flex-col items-center justify-center border-gray-100 border-l p-0.5">
                      <span className="text-emerald-600 text-[10px]">Hợp lệ</span>
                      <span className="font-bold font-mono text-emerald-600 text-sm">{stats.validNewCount}</span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Không tìm thấy trong từ điển">
                    <div className="flex cursor-help flex-col items-center justify-center border-gray-100 border-l p-0.5">
                      <span className="text-rose-600 text-[10px]">Không thấy</span>
                      <span className="font-bold font-mono text-rose-600 text-sm">{stats.notFoundCount}</span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Đã có trong bộ từ">
                    <div className="flex cursor-help flex-col items-center justify-center border-gray-100 border-l p-0.5">
                      <span className="text-blue-600 text-[10px]">Đã có</span>
                      <span className="font-bold font-mono text-blue-600 text-sm">{stats.alreadyInSetCount}</span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Trùng lặp trong tệp nhập">
                    <div className="flex cursor-help flex-col items-center justify-center border-gray-100 border-l p-0.5">
                      <span className="text-amber-600 text-[10px]">Trùng lặp</span>
                      <span className="font-bold font-mono text-amber-600 text-sm">{stats.duplicateCount}</span>
                    </div>
                  </Tooltip>
                </div>

                {/* Filter & Search Bar */}
                <div className="flex flex-wrap items-center justify-between gap-1.5">
                  <Radio.Group
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    size="small"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="all">Tất cả ({stats.total})</Radio.Button>
                    <Radio.Button value="valid_new">Hợp lệ ({stats.validNewCount})</Radio.Button>
                    <Radio.Button value="not_found">Không thấy ({stats.notFoundCount})</Radio.Button>
                    <Radio.Button value="already_in_target_set">Đã có ({stats.alreadyInSetCount})</Radio.Button>
                    <Radio.Button value="duplicate_in_input">Trùng ({stats.duplicateCount})</Radio.Button>
                  </Radio.Group>

                  <Input
                    placeholder="Tìm từ vựng, nghĩa, ví dụ..."
                    prefix={<SearchOutlined />}
                    size="small"
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 190 }}
                  />
                </div>

                {/* Table */}
                <div className="flex-1 min-h-0 bg-white rounded border border-gray-200 overflow-hidden flex flex-col p-1">
                  <Table
                    rowKey={(record, idx) => `${record.line_no}_${idx}`}
                    columns={outputColumns}
                    dataSource={filteredItems}
                    size="small"
                    bordered
                    classNames={{
                      root: "full-height-table",
                    }}
                    pagination={{
                      pageSize: 15,
                      showSizeChanger: true,
                      pageSizeOptions: ["15", "30", "50", "100"],
                      showTotal: (total) => `Tổng ${total} từ`,
                      size: "small",
                      style: { marginBottom: 2, marginTop: 6 },
                    }}
                    scroll={{ x: 900, y: "100%" }}
                  />
                </div>
              </>
            )}

            {!outputLoading && !outputError && !outputData && (
              <div className="flex-1 min-h-0 flex flex-col items-center justify-center bg-white rounded border border-dashed border-gray-200">
                <Empty description="Không có kết quả Output cho lượt này" />
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AddVocabularyDetailModal
